import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  overwriteRoutePaths,
} from '@leagueplatform/core';
import { Navigate, RouteObject } from 'react-router-dom';
import { ClaimsRoutes } from '@leagueplatform/claims';
import {
  HealthJourneyRoutes,
  ProgramOverviewRoutes,
} from '@leagueplatform/health-journey';
import { MilestonesRoutes } from '@leagueplatform/milestones';
import { BenefitsRoutes } from '@leagueplatform/benefits';
import {
  PreAuthorizationsLandingPage,
  PreAuthorizationDetailsPage,
  PreAuthorizationsLearnMorePage,
  PRE_AUTHORIZATIONS_ROUTES,
} from '@leagueplatform/pre-authorizations';
import {
  AuthViewLogout,
  AuthViewStartAgentSession,
} from 'pages/auth/auth.pages';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { MASONRY } from 'common/constants';
import { AuthRedirectPage } from 'pages/auth-redirect/auth-redirect.page';
import { PageNotFound } from 'pages/page-not-found.page';
import { HomePage } from 'pages/home/home.page';
import { SupportPage } from 'pages/support/support.page';
import { PremiumPaymentsPage } from 'pages/premium-payments/premium-payments.page';
import { FaqPage } from 'pages/faq/faq.page';
import { HealthAlertsPage } from 'pages/health-alerts/health-alerts.page';
import { PharmacyPage } from 'pages/pharmacy/pharmacy.page';
import { PerksPage } from 'pages/perks/perks.page';
import { WellnessRewardsPage } from 'pages/wellness-rewards/wellness-rewards.page';
import { SuggestedActivitiesPage } from 'pages/suggested-activities/suggested-activities.page';
import { PersonalInfoPage } from 'pages/personal-info/personal-info.page';
import { CommunicationPreferencesPage } from 'pages/communication-preferences/communication-preferences.page';
import { SwitchAccountsPage } from 'pages/switch-accounts/switch-accounts.page';
import { NotificationsPage } from 'pages/notifications/notifications.page';
import { AchievementsPage } from 'pages/achievements/achievements.page';
import { AuthSessionContainer } from 'components/auth-session-container/auth-session-container';
import { MemberPrefixRedirect } from 'components/member-prefix-redirect/member-prefix-redirect.component';

// Define League route config override
const leagueRoutesOverride = {
  [LEAGUE_MODULE_NAMES.assessment]: '/member/health-profile/questions',
  [LEAGUE_MODULE_NAMES.healthProfile]: '/member/health-profile',
  [LEAGUE_MODULE_NAMES.healthNeedPages]: '/health-need',
  [LEAGUE_MODULE_NAMES.onboarding]: '/onboarding',
};

overwriteRoutePaths(leagueRoutesOverride);

export const appRoutes: RouteObject[] = [
  {
    path: `${APP_MANAGED_ROUTES.SIGN_IN}/*`,
    element: <AuthRedirectPage />,
  },
  {
    path: `${APP_MANAGED_ROUTES.SIGN_OUT}/*`,
    element: <AuthViewLogout />,
  },
  {
    path: APP_MANAGED_ROUTES.START_LOGIN,
    element: <AuthViewStartAgentSession />,
  },
  {
    path: APP_MANAGED_ROUTES.LANDING_PAGE,
    element: <AuthRedirectPage />,
  },
  {
    element: <AuthSessionContainer />,
    children: [
      {
        path: APP_MANAGED_ROUTES.HOME,
        element: <HomePage />,
      },
      {
        path: APP_MANAGED_ROUTES.MEMBER_HOME,
        element: <Navigate to={APP_MANAGED_ROUTES.HOME} replace />,
      },
      {
        path: APP_MANAGED_ROUTES.HEALTH_ALERTS,
        element: <HealthAlertsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.SUPPORT,
        element: <SupportPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PHARMACY,
        element: <PharmacyPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PERKS,
        element: <PerksPage />,
      },
      {
        path: APP_MANAGED_ROUTES.WELLNESS_REWARDS,
        element: <WellnessRewardsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.SUGGESTED_ACTIVITIES,
        element: <SuggestedActivitiesPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PREMIUM_PAYMENTS,
        element: <PremiumPaymentsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.FAQ,
        element: <FaqPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PERSONAL_INFO,
        element: <PersonalInfoPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
        element: <CommunicationPreferencesPage />,
      },
      {
        path: APP_MANAGED_ROUTES.SWITCH_ACCOUNTS,
        element: <SwitchAccountsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.NOTIFICATIONS,
        element: <NotificationsPage />,
      },
      ...[
        `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.careLibrary)}/*`,
      ].map((path) => ({
        path,
        element: <HealthJourneyRoutes />, // Lazy loading is not used here due to Journey NodeRenderers required on the Homepage
      })),
      {
        path: '/member/health-programs',
        children: [{ index: true }, { path: '*' }].map((route) => ({
          ...route,
          element: (
            <MemberPrefixRedirect
              to={getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}
            />
          ),
        })),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.program)}/*`,
        element: <ProgramOverviewRoutes />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.challenge)}/*`,
        element: <ProgramOverviewRoutes />,
      },
      {
        path: getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements),
        element: <AchievementsPage />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/care-collections'))
            .CareCollectionsRoutes,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/care-discovery'))
            .CareDiscoveryRoutes,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthNeedPages)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/care-collections'))
            .HealthNeedPages,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.benefits)}/*`,
        element: <BenefitsRoutes />, // Lazy loading is not used here due to Benefits NodeRenderers required on the Homepage
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.claims)}/*`,
        element: <ClaimsRoutes />, // Lazy loading is not used here due to Claims NodeRenderers required on the Homepage
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.milestones)}/*`,
        element: <MilestonesRoutes />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/health-profile'))
            .HealthProfileRoutes,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/about-me')).AboutMeRoutes,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/messaging'))
            .MessagingRoutes,
        }),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/care-documents'))
            .CareDocumentsRoutes,
        }),
      },
      // Statically imported, unlike the SDK routes above: these cannot be
      // code-split. `init-masonry-registry.ts` has to import the capability at
      // startup to register its renderers before any tree mounts, which pins the
      // package into the entry chunk — with `lazy()` here rolldown reports
      // INEFFECTIVE_DYNAMIC_IMPORT (naming that registry import) and the entry
      // chunk grows instead of shrinking.
      {
        path: PRE_AUTHORIZATIONS_ROUTES.LANDING,
        element: (
          <PreAuthorizationsLandingPage
            rootNodeId={MASONRY.ROOT_NODE_ID_PRE_AUTHORIZATIONS}
          />
        ),
      },
      {
        path: PRE_AUTHORIZATIONS_ROUTES.DETAILS,
        element: (
          <PreAuthorizationDetailsPage
            rootNodeId={MASONRY.ROOT_NODE_ID_PRE_AUTHORIZATIONS_DETAILS}
          />
        ),
      },
      {
        path: PRE_AUTHORIZATIONS_ROUTES.LEARN_MORE,
        element: (
          <PreAuthorizationsLearnMorePage
            rootNodeId={MASONRY.ROOT_NODE_ID_PRE_AUTHORIZATIONS_LEARN_MORE}
          />
        ),
      },
      {
        path: '/*',
        element: <PageNotFound />,
      },
    ],
  },
];
